import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, MapPin, ArrowRight, Briefcase, ShieldCheck, Languages, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import { READY_PENDING_KEY } from './AccountReadyStep';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// iter98 (Feb 2026) — Worker onboarding step 1 now collects the Post
// Office-specific signals the best-fit matching engine needs. Per pilot
// spec (2b): one PRIMARY current role, optional multi-select of
// additional PO services, years-of-experience bucket, fluent-English
// yes/no, optional other-languages, home postcode and Smart ID status.
// We deliberately DO NOT ask workers to categorise every role as
// Current / Previous / Trained — that was too much friction for the
// pilot signup.

const YEARS_OPTIONS = [
  { value: 'less_than_1', label: 'Less than 1 year' },
  { value: '1_to_2', label: '1–2 years' },
  { value: '3_to_5', label: '3–5 years' },
  { value: '5_to_10', label: '5–10 years' },
  { value: '10_plus', label: '10+ years' },
];

const SMART_ID_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_sure', label: 'Not sure' },
];

const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export default function WorkerOnboarding() {
  const { api, user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    postcode: (user?.postcode || '').toUpperCase(),
    currentRole: user?.workerProfile?.currentPostOfficeRole || '',
    yearsExperience: user?.workerProfile?.yearsExperience || '',
    additionalAreas: user?.workerProfile?.additionalPostOfficeAreas || [],
    fluentEnglish: typeof user?.workerProfile?.fluentEnglish === 'boolean' ? user.workerProfile.fluentEnglish : null,
    otherLanguages: user?.workerProfile?.otherLanguages || [],
    smartId: user?.smartIdSelfDeclared === true ? 'yes' : '',
  });
  const [langDraft, setLangDraft] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/meta/post-office-vocab');
        setRoles(data.roles || []);
      } catch { /* silent — the UI degrades to a small hardcoded list */ }
    })();
  }, [api]);

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: undefined }));
  };

  const toggleAdditionalArea = (area) => {
    setForm(f => ({
      ...f,
      additionalAreas: f.additionalAreas.includes(area)
        ? f.additionalAreas.filter(a => a !== area)
        : [...f.additionalAreas, area],
    }));
  };

  const addLanguage = () => {
    const v = langDraft.trim();
    if (!v || form.otherLanguages.includes(v)) { setLangDraft(''); return; }
    setForm(f => ({ ...f, otherLanguages: [...f.otherLanguages, v] }));
    setLangDraft('');
  };

  const removeLanguage = (l) => setForm(f => ({ ...f, otherLanguages: f.otherLanguages.filter(x => x !== l) }));

  const validate = () => {
    const next = {};
    if (!form.postcode.trim()) next.postcode = 'Enter a valid UK postcode.';
    else if (!UK_POSTCODE_RE.test(form.postcode.trim())) next.postcode = 'Enter a valid UK postcode.';
    if (!form.currentRole) next.currentRole = 'Pick your current Post Office role.';
    if (!form.yearsExperience) next.yearsExperience = 'Pick how long you have been doing this.';
    if (form.fluentEnglish === null) next.fluentEnglish = 'Please answer this so managers can match language-specific shifts.';
    if (!form.smartId) next.smartId = 'Choose your Smart ID status.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // 1. Basic profile (postcode).
      await api.patch('/users/me', { postcode: form.postcode.trim().toUpperCase() });
      // 2. Post Office profile — the matching engine's input.
      await api.post('/worker/post-office-profile', {
        currentPostOfficeRole: form.currentRole,
        yearsExperience: form.yearsExperience,
        additionalPostOfficeAreas: form.additionalAreas.filter(a => a !== form.currentRole),
        fluentEnglish: !!form.fluentEnglish,
        otherLanguages: form.otherLanguages,
      });
      // 3. Smart ID self-declaration (only fire when the worker said yes).
      if (form.smartId === 'yes') {
        try { await api.patch('/users/me/smart-id'); } catch { /* non-blocking */ }
      }
      // 4. Mark onboarding done + show the AccountReady hand-off.
      await api.patch('/users/me/onboarding-complete');
      if (user?.id) {
        try { localStorage.setItem(READY_PENDING_KEY(user.id), '1'); } catch { /* storage may be blocked */ }
      }
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err, 'We could not save your details. Please try again.'));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="worker-onboarding">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          <OnboardingStepIndicator current={1} role="worker" />

          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 bg-[#DA291C] rounded-xl flex items-center justify-center shrink-0">
              <Briefcase className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Your Post Office experience</h2>
          </div>
          <p className="text-base text-slate-600 mb-6">
            A few Post Office-specific details so managers can pick the right cover.
          </p>

          <div className="space-y-6">
            {/* Home postcode */}
            <div className="space-y-1.5">
              <Label htmlFor="worker-postcode" className="text-base font-medium text-slate-800 flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-slate-500" /> Home postcode
              </Label>
              <Input
                id="worker-postcode"
                value={form.postcode}
                onChange={e => setField('postcode', e.target.value.toUpperCase())}
                placeholder="e.g., E1 6AN"
                className="h-12 rounded-xl text-base uppercase"
                data-testid="onboard-postcode"
                autoComplete="postal-code"
              />
              {errors.postcode && (<p className="text-sm text-red-600" data-testid="postcode-error">{errors.postcode}</p>)}
              <p className="text-xs text-slate-500 mt-1">Used only to find shifts close to you. Never shown to other workers.</p>
            </div>

            {/* Current Post Office role */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Current Post Office role</Label>
              <select
                value={form.currentRole}
                onChange={e => setField('currentRole', e.target.value)}
                className="w-full h-12 rounded-xl border-2 border-slate-200 px-3 text-base bg-white"
                data-testid="onboard-current-role"
              >
                <option value="">Select your current role…</option>
                {roles.map(r => (<option key={r} value={r}>{r}</option>))}
              </select>
              {errors.currentRole && (<p className="text-sm text-red-600 mt-1.5" data-testid="current-role-error">{errors.currentRole}</p>)}
            </div>

            {/* Years experience */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Years of Post Office experience</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="onboard-years-group">
                {YEARS_OPTIONS.map(o => {
                  const selected = form.yearsExperience === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setField('yearsExperience', o.value)}
                      aria-pressed={selected}
                      className={`min-h-[48px] px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${selected ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 text-slate-800 bg-white'}`}
                      data-testid={`onboard-years-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.yearsExperience && (<p className="text-sm text-red-600 mt-1.5" data-testid="years-error">{errors.yearsExperience}</p>)}
            </div>

            {/* Additional PO areas — optional multi-select */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Other Post Office services you&apos;ve done <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex flex-wrap gap-2" data-testid="onboard-additional-areas">
                {roles.filter(r => r !== form.currentRole).map(r => {
                  const selected = form.additionalAreas.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleAdditionalArea(r)}
                      aria-pressed={selected}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected ? 'border-[#0f1b3d] bg-[#0f1b3d] text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                      data-testid={`onboard-area-${r.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/,'')}`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Fluent English */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 flex items-center gap-1.5">
                <Languages className="h-4 w-4 text-slate-500" /> Are you fluent in English?
              </Label>
              <div className="grid grid-cols-2 gap-2" data-testid="onboard-fluent-english-group">
                {[{ v: true, label: 'Yes' }, { v: false, label: 'No' }].map(o => {
                  const selected = form.fluentEnglish === o.v;
                  return (
                    <button
                      key={String(o.v)}
                      type="button"
                      onClick={() => setField('fluentEnglish', o.v)}
                      aria-pressed={selected}
                      className={`min-h-[48px] rounded-xl border-2 text-base font-semibold transition-all ${selected ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 text-slate-800 bg-white'}`}
                      data-testid={`onboard-fluent-${o.v ? 'yes' : 'no'}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.fluentEnglish && (<p className="text-sm text-red-600 mt-1.5" data-testid="fluent-english-error">{errors.fluentEnglish}</p>)}
              <p className="text-xs text-slate-500 mt-2">Language only affects matching when a manager marks it as useful for a specific shift.</p>
            </div>

            {/* Other languages */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Other languages you speak <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex gap-2">
                <Input
                  value={langDraft}
                  onChange={e => setLangDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }}
                  placeholder="e.g., Polish"
                  className="h-11 rounded-xl text-base"
                  data-testid="onboard-language-input"
                />
                <Button
                  type="button"
                  onClick={addLanguage}
                  variant="outline"
                  className="h-11 rounded-xl px-3"
                  data-testid="onboard-language-add-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {form.otherLanguages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="onboard-language-chips">
                  {form.otherLanguages.map(l => (
                    <span key={l} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-sm border border-slate-200">
                      {l}
                      <button type="button" onClick={() => removeLanguage(l)} aria-label={`Remove ${l}`} className="text-slate-500 hover:text-slate-800">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Smart ID */}
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-slate-500" /> Do you have a Smart ID?
              </Label>
              <div className="grid grid-cols-3 gap-2" data-testid="onboard-smart-id-group">
                {SMART_ID_OPTIONS.map(o => {
                  const selected = form.smartId === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setField('smartId', o.value)}
                      aria-pressed={selected}
                      className={`min-h-[48px] px-3 py-3 rounded-xl border-2 text-base font-semibold transition-all ${selected ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 text-slate-800 bg-white'}`}
                      data-testid={`onboard-smart-id-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.smartId && (<p className="text-sm text-red-600 mt-1.5" data-testid="smart-id-error">{errors.smartId}</p>)}
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full h-12 rounded-full mt-7 bg-[#DA291C] hover:bg-[#b91f13] text-white text-base font-semibold disabled:opacity-60"
            data-testid="onboard-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
